import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import InternationalPhoneInput from '../../common/InternationalPhoneInput';
import { apiService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import './V3MultiCategoryTicketForm.scss';

interface Category {
  id: number;
  name: string;
  price: number;
  description?: string;
  sold_out: boolean;
  remaining: number;
  hidden: boolean;
  hide_on_soldout: boolean;
  cluster?: {
    id: number;
    name: string;
  };
  tag?: string;
}

interface EventField {
  id: number;
  name: string;
  label: string;
  field_type: string;
  required: boolean;
  options_dict?: { [key: string]: string };
}

interface V3MultiCategoryTicketFormProps {
  event: any;
  categories?: Category[];
  quota?: number;
  loading?: boolean;
  error?: string | null;
}

const V3MultiCategoryTicketForm: React.FC<V3MultiCategoryTicketFormProps> = ({
  event,
  categories = [],
  quota,
  loading = false,
  error = null
}) => {
  const { user, isAuthenticated } = useAuth();
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });
  const [customFields, setCustomFields] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showFloatingButton, setShowFloatingButton] = useState(false);
  const formSectionRef = useRef<HTMLDivElement>(null);
  const normalizeFilterValue = (value?: string) => value?.trim().toLowerCase() || '';

  // Memoize visibleCategories to prevent infinite loops
  const visibleCategories = useMemo(() => {
    return categories.filter(cat => !cat.hidden && !cat.hide_on_soldout);
  }, [categories]);

  // Initialize quantities only when categories change
  useEffect(() => {
    const initialQuantities: { [key: number]: number } = {};
    visibleCategories.forEach(category => {
      initialQuantities[category.id] = 0;
    });
    setQuantities(initialQuantities);
  }, [visibleCategories]);

  useEffect(() => {
    if (isAuthenticated && user) {
      const fullName = `${user.first_name} ${user.last_name}`.trim();
      const userPhone = user.profile?.phone_number || '';
      
      setFormData(prev => ({
        ...prev,
        name: prev.name || fullName,
        email: prev.email || user.email,
        phone: prev.phone || userPhone,
      }));
    }
  }, [isAuthenticated, user]);

  // Calculate total quantity for floating button visibility
  const totalQuantity = useMemo(() => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  }, [quantities]);

  useEffect(() => {
    const checkFormVisibility = () => {
      if (!formSectionRef.current) return;
      
      const isMobile = window.innerWidth <= 768;
      if (!isMobile) {
        setShowFloatingButton(false);
        return;
      }

      if (totalQuantity === 0) {
        setShowFloatingButton(false);
        return;
      }

      const rect = formSectionRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const buffer = 100;
      const isFormVisible = rect.top < (viewportHeight - buffer) && rect.bottom > buffer;
      
      setShowFloatingButton(!isFormVisible);
    };

    checkFormVisibility();
    window.addEventListener('scroll', checkFormVisibility, { passive: true });
    window.addEventListener('resize', checkFormVisibility, { passive: true });

    return () => {
      window.removeEventListener('scroll', checkFormVisibility);
      window.removeEventListener('resize', checkFormVisibility);
    };
  }, [totalQuantity]);

  const incrementQuantity = (categoryId: number) => {
    const category = visibleCategories.find(c => c.id === categoryId);
    if (!category || category.sold_out) return;

    const currentTotal = totalQuantity;
    const currentValue = quantities[categoryId] || 0;
    
    // If quota is undefined/null, no limit (use category remaining or a high number)
    if (quota === undefined || quota === null) {
      const maxAllowed = category.remaining || 999;
      const newValue = Math.min((currentValue || 0) + 1, maxAllowed);
      setQuantities(prev => ({
        ...prev,
        [categoryId]: newValue
      }));
      return;
    }
    
    // If quota is 0, don't allow increment
    if (quota === 0) return;
    
    // Calculate remaining quota (total quota minus all other quantities)
    const remaining = quota - currentTotal + currentValue;
    if (remaining > 0) {
      const newValue = Math.min((currentValue || 0) + 1, currentValue + remaining);
      setQuantities(prev => ({
        ...prev,
        [categoryId]: newValue
      }));
    }
  };

  const decrementQuantity = (categoryId: number) => {
    setQuantities(prev => ({
      ...prev,
      [categoryId]: Math.max((prev[categoryId] || 0) - 1, 0)
    }));
  };

  const handleQuantityChange = (categoryId: number, value: string) => {
    const numValue = parseInt(value) || 0;
    const category = visibleCategories.find(c => c.id === categoryId);
    if (!category || category.sold_out) return;

    // If quota is undefined/null, no limit (use category remaining or a high number)
    if (quota === undefined || quota === null) {
      const maxAllowed = category.remaining || 999;
      setQuantities(prev => ({
        ...prev,
        [categoryId]: Math.min(Math.max(numValue, 0), maxAllowed)
      }));
      return;
    }
    
    // If quota is 0, don't allow change
    if (quota === 0) return;

    const currentTotal = totalQuantity;
    const currentValue = quantities[categoryId] || 0;
    const remaining = quota - currentTotal + currentValue;

    setQuantities(prev => ({
      ...prev,
      [categoryId]: Math.min(Math.max(numValue, 0), remaining)
    }));
  };

  // Memoize getTotalQuantity to prevent unnecessary recalculations
  const getTotalQuantity = useCallback(() => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  }, [quantities]);

  const getTotalPrice = () => {
    return visibleCategories.reduce((total, category) => {
      const qty = quantities[category.id] || 0;
      const price = typeof category.price === 'string' ? parseFloat(category.price) : category.price;
      return total + ((isNaN(price) ? 0 : price) * qty);
    }, 0);
  };

  const formatPrice = (price: number | string) => {
    const symbol = event?.setting?.currency?.symbol || '$';
    // Ensure price is a number
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) {
      return `${symbol} 0.00`;
    }
    return `${symbol} ${numPrice.toFixed(2)}`;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (value: string | undefined) => {
    setFormData(prev => ({
      ...prev,
      phone: value || ''
    }));
  };

  const handleCustomFieldChange = (fieldName: string, value: string) => {
    setCustomFields(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const toggleDescription = (categoryId: number) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleFilterChange = (filterValue: string) => {
    setActiveFilter(filterValue);
  };

  const getFilteredCategories = () => {
    if (activeFilter === 'all') {
      return visibleCategories;
    }

    if (activeFilter.startsWith('cluster:')) {
      const clusterName = activeFilter.replace('cluster:', '');
      const normalizedCluster = normalizeFilterValue(clusterName);
      return visibleCategories.filter(cat => normalizeFilterValue(cat.cluster?.name) === normalizedCluster);
    }

    if (activeFilter.startsWith('tag:')) {
      const tagName = activeFilter.replace('tag:', '');
      const normalizedTag = normalizeFilterValue(tagName);
      return visibleCategories.filter(cat => normalizeFilterValue(cat.tag) === normalizedTag);
    }

    return visibleCategories;
  };

  const getSelectedItems = () => {
    return visibleCategories
      .filter(category => (quantities[category.id] || 0) > 0)
      .map(category => {
        const price = typeof category.price === 'string' ? parseFloat(category.price) : category.price;
        const numPrice = isNaN(price) ? 0 : price;
        const qty = quantities[category.id];
        return {
          name: category.name,
          quantity: qty,
          price: numPrice,
          total: numPrice * qty
        };
      });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (totalQuantity === 0) {
      setSubmitError('Please select at least one ticket');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const selectedCategories = visibleCategories
        .filter(category => (quantities[category.id] || 0) > 0)
        .map(category => ({
          category_id: category.id,
          quantity: quantities[category.id],
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          custom_fields: customFields
        }));

      const registrationData = {
        event_slug: event.slug,
        categories: selectedCategories
      };

      const response = await apiService.registration.multi(registrationData);
      
      if (response.success) {
        window.location.href = response.redirect_url || '/cart';
      } else {
        setSubmitError(response.message || 'Registration failed');
      }
    } catch (error: any) {
      setSubmitError(error.message || 'An error occurred during registration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = useCallback((e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (formSectionRef.current) {
      formSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const renderCustomField = (field: EventField) => {
    if (field.field_type === 'select') {
      return (
        <div key={field.id} className="form-group">
          <label htmlFor={field.name}>
            {field.field_type === 'select' ? (
              <i className="fas fa-list"></i>
            ) : (
              <i className="fas fa-edit"></i>
            )}
            {field.label}
          </label>
          <select
            className="form-select"
            name={field.name}
            id={field.name}
            value={customFields[field.name] || ''}
            onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options_dict && Object.entries(field.options_dict).map(([key, value]) => (
              <option key={value} value={value}>{key}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.id} className="form-group">
        <label htmlFor={field.name}>
          <i className="fas fa-edit"></i>
          {field.label}
        </label>
        <input
          type={field.field_type}
          className="form-control"
          name={field.name}
          id={field.name}
          placeholder={field.label}
          value={customFields[field.name] || ''}
          onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
          required={field.required}
        />
      </div>
    );
  };

  // Get unique clusters and tags for filters
  const clusters = useMemo(() => {
    const uniqueClusters = new Map<string, { id: number; name: string }>();
    visibleCategories.forEach(category => {
      if (!category.cluster) return;
      const trimmedName = category.cluster.name?.trim();
      if (!trimmedName) return;
      const key = category.cluster.id
        ? `id:${category.cluster.id}`
        : `name:${normalizeFilterValue(trimmedName)}`;
      if (!uniqueClusters.has(key)) {
        uniqueClusters.set(key, { ...category.cluster, name: trimmedName });
      }
    });
    return Array.from(uniqueClusters.values());
  }, [visibleCategories]);

  const tags = useMemo(() => {
    const uniqueTags = new Map<string, string>();
    visibleCategories.forEach(category => {
      const trimmedTag = category.tag?.trim();
      if (!trimmedTag) return;
      const key = normalizeFilterValue(trimmedTag);
      if (!uniqueTags.has(key)) {
        uniqueTags.set(key, trimmedTag);
      }
    });
    return Array.from(uniqueTags.values());
  }, [visibleCategories]);

  if (loading) {
    return (
      <div className="v3-multi-category-form">
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading ticket form...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v3-multi-category-form">
        <div className="alert alert-warning" role="alert">
          <strong>Ticket Form Loading Error:</strong> {error}
        </div>
      </div>
    );
  }

  const filteredCategories = getFilteredCategories();
  const selectedItems = getSelectedItems();

  return (
    <div className="v3-multi-category-form">
      {/* Floating Button */}
      {showFloatingButton && (
        <button 
          id="floating-form-button" 
          type="button"
          className="floating-form-button visible" 
          onClick={scrollToForm}
        >
          <i className="fas fa-arrow-down"></i>
          <span className="button-text">Go to Form</span>
          <span className="button-badge">{totalQuantity}</span>
        </button>
      )}

      {/* Filter Bar */}
      {(clusters.length > 0 || tags.length > 0) && (
        <div className="category-filters">
          <div className="filter-group">
            <label className="filter-label">
              <i className="fas fa-filter"></i>
              Filter
            </label>
            <div className="filter-buttons">
              <button
                type="button"
                className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                data-filter-value="all"
                onClick={() => handleFilterChange('all')}
              >
                All
              </button>
              {clusters.map((cluster: any) => (
                <button
                  key={cluster.id}
                  type="button"
                  className={`filter-btn ${activeFilter === `cluster:${cluster.name}` ? 'active' : ''}`}
                  data-filter-value={`cluster:${cluster.name}`}
                  onClick={() => handleFilterChange(`cluster:${cluster.name}`)}
                >
                  {cluster.name}
                </button>
              ))}
              {tags.map((tag: string) => (
                <button
                  key={tag}
                  type="button"
                  className={`filter-btn ${activeFilter === `tag:${tag}` ? 'active' : ''}`}
                  data-filter-value={`tag:${tag}`}
                  onClick={() => handleFilterChange(`tag:${tag}`)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} id="registration-form">
        {quota === 0 && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Registration unavailable: Your event quota has been reached.
          </div>
        )}

        <div className="registration-layout">
          {/* Categories Section */}
          <div className="categories-section">
            <div className="categories-grid">
              {filteredCategories.length === 0 ? (
                <div className="no-results-message">
                  <i className="fas fa-search"></i>
                  <p>No categories match the selected filter.</p>
                </div>
              ) : (
                filteredCategories.map((category) => (
                  <div
                    key={category.id}
                    className={`category-item ${category.sold_out ? 'sold-out' : ''} ${(quantities[category.id] || 0) > 0 ? 'selected' : ''}`}
                    data-cluster={category.cluster?.name || 'none'}
                    data-tag={category.tag || 'none'}
                    data-category-id={category.id}
                    data-category-name={category.name}
                    data-category-price={category.price}
                  >
                    <div className="category-header">
                      <div className="category-info">
                        <div className="category-badges">
                          {category.cluster && (
                            <span className="category-badge cluster-badge">{category.cluster.name}</span>
                          )}
                          {category.tag && (
                            <span className="category-badge tag-badge">{category.tag}</span>
                          )}
                        </div>
                      </div>
                      <div className="quantity-controls">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            decrementQuantity(category.id);
                          }}
                          disabled={category.sold_out || (quantities[category.id] || 0) <= 0}
                        >
                          <i className="fas fa-minus"></i>
                        </button>
                        <input
                          type="number"
                          id={`quantity_${category.id}`}
                          name="quantities[]"
                          value={quantities[category.id] || 0}
                          min="0"
                          max={quota !== undefined && quota !== null ? quota : (category.remaining || 999)}
                          disabled={category.sold_out || quota === 0}
                          onChange={(e) => handleQuantityChange(category.id, e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            incrementQuantity(category.id);
                          }}
                          disabled={
                            category.sold_out || 
                            quota === 0 ||
                            (quota !== undefined && quota !== null && totalQuantity >= quota)
                          }
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                    <div>
                      <h6>{category.name}</h6>
                      <div className="price-container">
                        <p className="price">{formatPrice(category.price)}</p>
                        {event?.setting?.show_remaining_passes && (
                          <span className="remaining-passes">
                            <i className="fas fa-ticket-alt"></i>
                            {category.remaining} Left
                          </span>
                        )}
                      </div>
                    </div>
                    {category.description && (
                      <div className="description-toggle-container">
                        <button
                          type="button"
                          className="description-toggle-btn"
                          onClick={() => toggleDescription(category.id)}
                          aria-expanded={expandedDescriptions.has(category.id)}
                        >
                          <i className="fas fa-info-circle"></i>
                          <span className="toggle-text">
                            {expandedDescriptions.has(category.id) ? 'Hide Details...' : 'More Details...'}
                          </span>
                          <i className={`fas fa-chevron-${expandedDescriptions.has(category.id) ? 'up' : 'down'} toggle-icon`}></i>
                        </button>
                        {expandedDescriptions.has(category.id) && (
                          <div className="category-description" id={`description_${category.id}`}>
                            <div dangerouslySetInnerHTML={{ __html: category.description || '' }} />
                          </div>
                        )}
                      </div>
                    )}
                    {!category.sold_out && (
                      <input type="hidden" name="categories[]" value={category.id} />
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Form & Summary Section */}
          <div className="form-section" ref={formSectionRef}>
            {/* Order Summary */}
            <div className="order-summary">
              <h5 className="summary-title">
                <i className="fas fa-shopping-cart"></i>
                Order Summary
              </h5>
              <div id="selected-items-list" className="selected-items-list">
                {selectedItems.length === 0 ? (
                  <div className="empty-summary">
                    <i className="fas fa-ticket-alt"></i>
                    <p>No items selected</p>
                    <span className="empty-hint">Select categories above to add tickets</span>
                  </div>
                ) : (
                  selectedItems.map((item, index) => (
                    <div key={index} className="selected-item">
                      <div className="item-info">
                        <span className="item-name">{item.name}</span>
                        <span className="item-quantity">× {item.quantity}</span>
                      </div>
                      <div className="item-price">{formatPrice(item.total)}</div>
                    </div>
                  ))
                )}
              </div>
              <div className="summary-totals">
                <div className="total-row">
                  <span className="total-label">Total Tickets:</span>
                  <span className="total-value" id="summary-total-quantity">{totalQuantity}</span>
                </div>
                <div className="total-row">
                  <span className="total-label">Total Price:</span>
                  <span className="total-value price" id="summary-total-price">{formatPrice(getTotalPrice())}</span>
                </div>
              </div>
            </div>

            {/* Registration Form */}
            <div className="registration-form-section">
              <h5 className="form-section-title">
                <i className="fas fa-user"></i>
                Registration Details
              </h5>
              <div className="form-fields">
                <div className="form-group">
                  <label htmlFor="name">
                    <i className="fas fa-user"></i>
                    Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    id="name"
                    placeholder="Pass Holder Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope"></i>
                    Email
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    id="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">
                    <i className="fas fa-phone"></i>
                    Phone
                  </label>
                  <InternationalPhoneInput
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="Phone Number"
                    id="phone"
                    name="phone"
                    label=""
                    showLabel={false}
                    required={true}
                  />
                </div>
                {event?.fields?.map((fieldItem: any) => {
                  const field = fieldItem.field || fieldItem;
                  return renderCustomField(field);
                })}
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              {quota === 0 ? (
                <>
                  <div className="alert alert-warning">
                    You've reached the quota for this event. Check your{' '}
                    <a href="/cart" className="alert-link" onClick={(e) => {
                      e.preventDefault();
                      window.location.href = '/cart';
                    }}>cart</a> to see if you already have tickets.
                  </div>
                </>
              ) : (
                <>
                  {submitError && (
                    <div className="alert alert-danger mb-3">
                      {submitError}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="btn btn-primary btn-submit"
                    id="submit-btn"
                    disabled={totalQuantity === 0 || isSubmitting}
                  >
                    <i className="fas fa-check-circle"></i>
                    {isSubmitting ? 'Processing...' : (event?.content?.button_label || 'Add to Cart')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default V3MultiCategoryTicketForm;

